import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Task, TaskType, CreateTaskRequest } from '../../types';
import { apiService } from '../../services/api';
import RescheduleModal from './RescheduleModal';

interface Category {
  id: string;
  name: string;
  color: string;
  text_color: string;
}

interface MergedPlannerProps {
  selectedDate: string;
  onDateChange?: (date: string) => void;
}

interface HourSlot {
  hour: number;
  half: 'first' | 'second';
}

interface DragSelection {
  start: HourSlot | null;
  end: HourSlot | null;
  isSelecting: boolean;
}

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (task: CreateTaskRequest) => void;
  selectedDate: string;
  categories: Category[];
  prefilledStart?: { hour: number; minute: number };
  prefilledEnd?: { hour: number; minute: number };
}

const AddTaskModal: React.FC<AddTaskModalProps> = ({ 
  isOpen, onClose, onSubmit, selectedDate, categories, 
  prefilledStart, prefilledEnd 
}) => {
  const [title, setTitle] = useState('');
  const [taskType, setTaskType] = useState<TaskType>('hourly');
  const [startHour, setStartHour] = useState(prefilledStart?.hour ?? 9);
  const [startMinute, setStartMinute] = useState(prefilledStart?.minute ?? 0);
  const [endHour, setEndHour] = useState(prefilledEnd?.hour ?? 10);
  const [endMinute, setEndMinute] = useState(prefilledEnd?.minute ?? 0);
  const [priority, setPriority] = useState(2);
  const [label, setLabel] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  useEffect(() => {
    if (prefilledStart) {
      setStartHour(prefilledStart.hour);
      setStartMinute(prefilledStart.minute);
      setTaskType('hourly');
    }
    if (prefilledEnd) {
      setEndHour(prefilledEnd.hour);
      setEndMinute(prefilledEnd.minute);
    }
  }, [prefilledStart, prefilledEnd]);

  const resetForm = () => {
    setTitle('');
    setTaskType('hourly');
    setStartHour(9);
    setStartMinute(0);
    setEndHour(10);
    setEndMinute(0);
    setPriority(2);
    setLabel('');
    setSelectedCategoryId(null);
  };

  const handleSubmit = () => {
    if (!title.trim()) return;

    const formatTime = (h: number, m: number) => 
      `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`;

    const task: CreateTaskRequest = {
      title: title.trim(),
      scheduledDate: selectedDate,
      taskType,
      priority,
      label: label || undefined,
      categoryId: selectedCategoryId || undefined,
    };

    if (taskType === 'hourly') {
      task.scheduledHour = startHour;
      task.startTime = formatTime(startHour, startMinute);
      task.endTime = formatTime(endHour, endMinute);
      const startMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;
      task.estimatedDurationMinutes = endMinutes - startMinutes;
    } else if (taskType === 'time_block') {
      task.scheduledHour = startHour;
      task.startTime = formatTime(startHour, 0);
      task.endTime = formatTime(endHour, 0);
      task.estimatedDurationMinutes = (endHour - startHour) * 60;
    } else {
      task.scheduledHour = 0;
      task.estimatedDurationMinutes = 60;
    }

    onSubmit(task);
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = [0, 15, 30, 45];

  const formatHourDisplay = (h: number) => {
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayHour} ${period}`;
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '16px', padding: '24px',
        width: '90%', maxWidth: '480px', maxHeight: '90vh', overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Add New Task</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>×</button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#374151' }}>Task Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What do you want to do?"
            data-testid="task-title-input"
            style={{
              width: '100%', padding: '12px', border: '2px solid #e5e7eb',
              borderRadius: '8px', fontSize: '1rem', boxSizing: 'border-box'
            }}
            autoFocus
          />
        </div>

        {/* Task Type Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '12px', fontWeight: 600, color: '#374151' }}>When?</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <label style={{
              display: 'flex', alignItems: 'center', padding: '12px',
              border: `2px solid ${taskType === 'unscheduled' ? 'var(--primary-color, #667eea)' : '#e5e7eb'}`,
              borderRadius: '8px', cursor: 'pointer',
              backgroundColor: taskType === 'unscheduled' ? '#f0f4ff' : 'white'
            }}>
              <input type="radio" name="taskType" checked={taskType === 'unscheduled'} onChange={() => setTaskType('unscheduled')} style={{ marginRight: '12px' }} />
              <div>
                <div style={{ fontWeight: 600 }}>No specific time</div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Just add to today&apos;s list</div>
              </div>
            </label>

            <label style={{
              display: 'flex', alignItems: 'center', padding: '12px',
              border: `2px solid ${taskType === 'hourly' ? 'var(--primary-color, #667eea)' : '#e5e7eb'}`,
              borderRadius: '8px', cursor: 'pointer',
              backgroundColor: taskType === 'hourly' ? '#f0f4ff' : 'white'
            }}>
              <input type="radio" name="taskType" checked={taskType === 'hourly'} onChange={() => setTaskType('hourly')} style={{ marginRight: '12px' }} />
              <div>
                <div style={{ fontWeight: 600 }}>Specific time</div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>e.g., 4:15 PM to 4:45 PM</div>
              </div>
            </label>

            <label style={{
              display: 'flex', alignItems: 'center', padding: '12px',
              border: `2px solid ${taskType === 'time_block' ? 'var(--primary-color, #667eea)' : '#e5e7eb'}`,
              borderRadius: '8px', cursor: 'pointer',
              backgroundColor: taskType === 'time_block' ? '#f0f4ff' : 'white'
            }}>
              <input type="radio" name="taskType" checked={taskType === 'time_block'} onChange={() => setTaskType('time_block')} style={{ marginRight: '12px' }} />
              <div>
                <div style={{ fontWeight: 600 }}>Time block</div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>e.g., Meetings from 9 AM to 12 PM</div>
              </div>
            </label>
          </div>
        </div>

        {/* Time Selection */}
        {(taskType === 'hourly' || taskType === 'time_block') && (
          <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', color: '#6b7280' }}>Start</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select value={startHour} onChange={(e) => setStartHour(parseInt(e.target.value))} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                    {hours.map(h => <option key={h} value={h}>{formatHourDisplay(h)}</option>)}
                  </select>
                  {taskType === 'hourly' && (
                    <select value={startMinute} onChange={(e) => setStartMinute(parseInt(e.target.value))} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                      {minutes.map(m => <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>)}
                    </select>
                  )}
                </div>
              </div>
              <div style={{ fontSize: '1.5rem', color: '#9ca3af' }}>→</div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', color: '#6b7280' }}>End</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select value={endHour} onChange={(e) => setEndHour(parseInt(e.target.value))} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                    {hours.map(h => <option key={h} value={h}>{formatHourDisplay(h)}</option>)}
                  </select>
                  {taskType === 'hourly' && (
                    <select value={endMinute} onChange={(e) => setEndMinute(parseInt(e.target.value))} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                      {minutes.map(m => <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>)}
                    </select>
                  )}
                </div>
              </div>
            </div>
            {endHour > startHour && taskType === 'time_block' && (
              <div style={{ marginTop: '12px', fontSize: '0.875rem', color: '#059669' }}>
                Duration: {endHour - startHour} hour{endHour - startHour > 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}

        {/* Priority */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#374151' }}>Priority</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            {[
              { value: 1, label: 'Low', color: '#10b981' },
              { value: 2, label: 'Medium', color: '#f59e0b' },
              { value: 3, label: 'High', color: '#ef4444' }
            ].map(p => (
              <button
                key={p.value}
                onClick={() => setPriority(p.value)}
                style={{
                  flex: 1, padding: '10px',
                  border: `2px solid ${priority === p.value ? p.color : '#e5e7eb'}`,
                  borderRadius: '8px',
                  backgroundColor: priority === p.value ? `${p.color}15` : 'white',
                  color: priority === p.value ? p.color : '#6b7280',
                  fontWeight: 600, cursor: 'pointer'
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Category Selection */}
        {categories.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#374151' }}>Category</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setSelectedCategoryId(null)}
                style={{
                  padding: '8px 14px',
                  border: `2px solid ${!selectedCategoryId ? 'var(--primary-color, #667eea)' : '#e5e7eb'}`,
                  borderRadius: '20px',
                  backgroundColor: !selectedCategoryId ? 'var(--primary-light, #f0f4ff)' : 'white',
                  color: !selectedCategoryId ? 'var(--primary-color, #667eea)' : '#6b7280',
                  fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer'
                }}
              >
                None
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategoryId(cat.id)}
                  style={{
                    padding: '8px 14px',
                    border: `2px solid ${selectedCategoryId === cat.id ? cat.color : 'transparent'}`,
                    borderRadius: '20px',
                    backgroundColor: cat.color,
                    color: cat.text_color,
                    fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
                    transform: selectedCategoryId === cat.id ? 'scale(1.05)' : 'scale(1)',
                    transition: 'transform 0.15s'
                  }}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '14px', border: '2px solid #e5e7eb',
              borderRadius: '8px', backgroundColor: 'white',
              fontSize: '1rem', fontWeight: 600, cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim()}
            data-testid="add-task-submit"
            style={{
              flex: 1, padding: '14px', border: 'none', borderRadius: '8px',
              backgroundColor: title.trim() ? 'var(--primary-color, #667eea)' : '#d1d5db',
              color: 'white', fontSize: '1rem', fontWeight: 600,
              cursor: title.trim() ? 'pointer' : 'not-allowed'
            }}
          >
            Add Task
          </button>
        </div>
      </div>
    </div>
  );
};

const MergedPlanner: React.FC<MergedPlannerProps> = ({ selectedDate: initialDate, onDateChange }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [rescheduleTask, setRescheduleTask] = useState<Task | null>(null);
  const [dragSelection, setDragSelection] = useState<DragSelection>({ start: null, end: null, isSelecting: false });
  const [prefilledTime, setPrefilledTime] = useState<{ start?: { hour: number; minute: number }; end?: { hour: number; minute: number } }>({});
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);
  
  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);
  
  // Inline editing state
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingNotes, setEditingNotes] = useState('');
  const [editingDate, setEditingDate] = useState('');
  
  // Row width state (persisted in localStorage)
  const [rowWidth, setRowWidth] = useState<number>(() => {
    const saved = localStorage.getItem('plannerRowWidth');
    return saved ? parseInt(saved, 10) : 900;
  });
  const [isResizing, setIsResizing] = useState(false);
  const rowWidthRef = React.useRef(rowWidth);

  // Keep ref in sync with state
  React.useEffect(() => {
    rowWidthRef.current = rowWidth;
  }, [rowWidth]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = rowWidth;
    
    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(600, Math.min(1400, startWidth + (e.clientX - startX)));
      setRowWidth(newWidth);
      rowWidthRef.current = newWidth;
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      // Use ref to get the latest width value
      localStorage.setItem('plannerRowWidth', rowWidthRef.current.toString());
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // ===== Calendar grid constants, helpers & interaction state =====
  const HOUR_HEIGHT = 80;
  const PX_PER_MIN = HOUR_HEIGHT / 60;
  const [snap, setSnap] = useState(15);
  const [workStart, setWorkStart] = useState(7);
  const [workEnd, setWorkEnd] = useState(22);
  const [showMineOnly, setShowMineOnly] = useState(false);
  const currentUserId = apiService.getCurrentUser()?.id;
  const DAY_MINUTES = 24 * 60;
  const gridContentRef = useRef<HTMLDivElement>(null);

  const pad2 = (n: number) => n.toString().padStart(2, '0');
  const minToTime = (min: number) => {
    const m = ((Math.round(min) % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
    return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}:00`;
  };
  const clampNum = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

  const getTaskStartMin = (t: Task) => {
    if (t.start_time) {
      const [h, m] = t.start_time.split(':').map(Number);
      if (!isNaN(h)) return h * 60 + (m || 0);
    }
    return (t.scheduled_hour || 0) * 60;
  };
  const getTaskEndMin = (t: Task) => {
    const s = getTaskStartMin(t);
    if (t.end_time) {
      const [h, m] = t.end_time.split(':').map(Number);
      if (!isNaN(h)) {
        const e = h * 60 + (m || 0);
        if (e > s) return e;
      }
    }
    const dur = t.estimated_duration_minutes && t.estimated_duration_minutes > 0 ? t.estimated_duration_minutes : 60;
    return s + dur;
  };

  const [dragPreview, setDragPreview] = useState<{ taskId: string; startMin: number; endMin: number } | null>(null);
  const dragPreviewRef = useRef<{ taskId: string; startMin: number; endMin: number } | null>(null);
  useEffect(() => { dragPreviewRef.current = dragPreview; }, [dragPreview]);
  const [blockDrag, setBlockDrag] = useState<{ taskId: string; mode: 'move' | 'resize'; startY: number; origStart: number; origEnd: number } | null>(null);

  const [createDrag, setCreateDrag] = useState<{ startMin: number; curMin: number } | null>(null);
  const createDragRef = useRef<{ startMin: number; curMin: number } | null>(null);
  useEffect(() => { createDragRef.current = createDrag; }, [createDrag]);

  // Current-time "now line" (updates every 30s)
  const [nowMin, setNowMin] = useState<number>(() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); });
  useEffect(() => {
    const tick = () => { const d = new Date(); setNowMin(d.getHours() * 60 + d.getMinutes()); };
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  // Auto-scroll the now-line into view when opening today
  const nowLineRef = useRef<HTMLDivElement>(null);
  const didAutoScrollRef = useRef(false);
  useEffect(() => {
    const todayStr = new Date().toISOString().split('T')[0];
    if (selectedDate !== todayStr) { didAutoScrollRef.current = false; return; }
    if (didAutoScrollRef.current) return;
    const id = setTimeout(() => {
      if (nowLineRef.current) {
        nowLineRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        didAutoScrollRef.current = true;
      }
    }, 400);
    return () => clearTimeout(id);
  }, [selectedDate, loading, tasks.length]);

  // Tasks that overlap another task in time (double-booked)
  const getOverlappingIds = (items: Task[]) => {
    const ivs = items.map(t => ({ id: t.id, s: getTaskStartMin(t), e: getTaskEndMin(t) }));
    const set = new Set<string>();
    for (let i = 0; i < ivs.length; i++) {
      for (let j = i + 1; j < ivs.length; j++) {
        if (ivs[i].s < ivs[j].e && ivs[j].s < ivs[i].e) { set.add(ivs[i].id); set.add(ivs[j].id); }
      }
    }
    return set;
  };

  const yToMin = (clientY: number) => {
    const rect = gridContentRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    return clampNum(Math.round(((clientY - rect.top) / PX_PER_MIN) / snap) * snap, 0, DAY_MINUTES);
  };

  const commitTaskTime = async (taskId: string, startMin: number, endMin: number, existingType?: string) => {
    const duration = clampNum(endMin - startMin, snap, 480);
    try {
      await apiService.updateTask(taskId, {
        taskType: existingType && existingType !== 'unscheduled' ? existingType : 'hourly',
        scheduledHour: Math.floor(startMin / 60),
        startTime: minToTime(startMin),
        endTime: minToTime(startMin + duration),
        estimatedDurationMinutes: duration
      });
      await fetchTasks();
    } catch (e) {
      console.error('Failed to update task time:', e);
      alert('Could not update the task time. Please try again.');
    }
  };

  // Window listeners for move/resize
  useEffect(() => {
    if (!blockDrag) return;
    const onMove = (e: MouseEvent) => {
      const deltaMin = Math.round(((e.clientY - blockDrag.startY) / PX_PER_MIN) / snap) * snap;
      if (blockDrag.mode === 'move') {
        const dur = blockDrag.origEnd - blockDrag.origStart;
        const ns = clampNum(blockDrag.origStart + deltaMin, 0, DAY_MINUTES - dur);
        setDragPreview({ taskId: blockDrag.taskId, startMin: ns, endMin: ns + dur });
      } else {
        const ne = clampNum(blockDrag.origEnd + deltaMin, blockDrag.origStart + snap, DAY_MINUTES);
        setDragPreview({ taskId: blockDrag.taskId, startMin: blockDrag.origStart, endMin: ne });
      }
    };
    const onUp = () => {
      const preview = dragPreviewRef.current;
      const t = tasks.find(x => x.id === blockDrag.taskId);
      const changed = preview && (preview.startMin !== blockDrag.origStart || preview.endMin !== blockDrag.origEnd);
      setBlockDrag(null);
      setDragPreview(null);
      if (preview && t && changed) {
        commitTaskTime(preview.taskId, preview.startMin, preview.endMin, t.task_type);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockDrag, tasks, snap]);

  const startBlockMove = (e: React.MouseEvent, task: Task) => {
    if (editingTaskId === task.id) return;
    e.preventDefault();
    e.stopPropagation();
    setBlockDrag({ taskId: task.id, mode: 'move', startY: e.clientY, origStart: getTaskStartMin(task), origEnd: getTaskEndMin(task) });
  };
  const startBlockResize = (e: React.MouseEvent, task: Task) => {
    e.preventDefault();
    e.stopPropagation();
    setBlockDrag({ taskId: task.id, mode: 'resize', startY: e.clientY, origStart: getTaskStartMin(task), origEnd: getTaskEndMin(task) });
  };

  const bgMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const min = yToMin(e.clientY);
    setCreateDrag({ startMin: min, curMin: min + snap });
  };
  const bgMouseMove = (e: React.MouseEvent) => {
    if (!createDragRef.current) return;
    const min = yToMin(e.clientY);
    setCreateDrag(prev => (prev ? { ...prev, curMin: min } : prev));
  };
  const bgMouseUp = () => {
    const cd = createDragRef.current;
    setCreateDrag(null);
    if (!cd) return;
    let start = Math.min(cd.startMin, cd.curMin);
    let end = Math.max(cd.startMin, cd.curMin);
    if (end - start < snap) end = start + 60;
    end = Math.min(end, DAY_MINUTES);
    setPrefilledTime({
      start: { hour: Math.floor(start / 60), minute: start % 60 },
      end: { hour: Math.floor(end / 60), minute: end % 60 }
    });
    setShowAddModal(true);
  };

  const bgDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedTask) return;
    const startMin = yToMin(e.clientY);
    const origStart = getTaskStartMin(draggedTask);
    const origEnd = getTaskEndMin(draggedTask);
    let dur = origEnd - origStart;
    if (!dur || dur <= 0) dur = draggedTask.estimated_duration_minutes || 60;
    dur = clampNum(dur, snap, 480);
    const endMin = Math.min(startMin + dur, DAY_MINUTES);
    const t = draggedTask;
    setDraggedTask(null);
    await commitTaskTime(t.id, startMin, endMin, t.task_type);
  };

  const computeLayout = (items: Task[]) => {
    const intervals = items.map(t => ({ id: t.id, start: getTaskStartMin(t), end: getTaskEndMin(t), col: 0 }));
    intervals.sort((a, b) => a.start - b.start || a.end - b.end);
    const placement: Record<string, { col: number; total: number }> = {};
    let cluster: typeof intervals = [];
    let clusterMaxEnd = -1;
    const flush = () => {
      const cols: number[] = [];
      for (const it of cluster) {
        let placed = false;
        for (let c = 0; c < cols.length; c++) {
          if (it.start >= cols[c]) { it.col = c; cols[c] = it.end; placed = true; break; }
        }
        if (!placed) { it.col = cols.length; cols.push(it.end); }
      }
      const total = Math.max(1, cols.length);
      for (const it of cluster) placement[it.id] = { col: it.col, total };
    };
    for (const it of intervals) {
      if (cluster.length && it.start >= clusterMaxEnd) { flush(); cluster = []; clusterMaxEnd = -1; }
      cluster.push(it);
      clusterMaxEnd = Math.max(clusterMaxEnd, it.end);
    }
    if (cluster.length) flush();
    return placement;
  };

  const handleReschedule = async (payload: { date: string; unscheduled: boolean; startHour: number; startMinute: number; endHour: number; endMinute: number }) => {
    if (!rescheduleTask) return;
    try {
      if (payload.unscheduled) {
        await apiService.rescheduleTask(rescheduleTask.id, {
          newDate: payload.date,
          taskType: 'unscheduled'
        });
      } else {
        const startTotal = payload.startHour * 60 + payload.startMinute;
        const endTotal = payload.endHour * 60 + payload.endMinute;
        const duration = clampNum(endTotal - startTotal, snap, 480);
        await apiService.rescheduleTask(rescheduleTask.id, {
          newDate: payload.date,
          newHour: payload.startHour,
          newStartTime: minToTime(startTotal),
          newEndTime: minToTime(startTotal + duration),
          newDurationMinutes: duration,
          taskType: (rescheduleTask.task_type && rescheduleTask.task_type !== 'unscheduled') ? rescheduleTask.task_type : 'hourly'
        });
      }
      setRescheduleTask(null);
      await fetchTasks();
    } catch (error) {
      console.error('Failed to reschedule task:', error);
      alert('Failed to reschedule task. Please try again.');
    }
  };

  const handleDateChange = (newDate: string) => {
    setSelectedDate(newDate);
    if (onDateChange) onDateChange(newDate);
  };

  const goToPreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    handleDateChange(date.toISOString().split('T')[0]);
  };

  const goToNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    handleDateChange(date.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    handleDateChange(new Date().toISOString().split('T')[0]);
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  // Priority display helper (1=High, 2=Medium, 3=Low based on task creation UI)
  const getPriorityBadge = (priority: number) => {
    switch (priority) {
      case 3: return { emoji: '🔥', label: 'High', color: '#dc2626', bg: '#fee2e2' };
      case 2: return { emoji: '⚡', label: 'Medium', color: '#d97706', bg: '#fef3c7' };
      case 1: return { emoji: '🌱', label: 'Low', color: '#16a34a', bg: '#dcfce7' };
      default: return null;
    }
  };

  const fetchCategories = useCallback(async () => {
    try {
      const response = await apiService.getCategories();
      if (response.success) setCategories(response.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getTasks(selectedDate);
      setTasks(response.tasks || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // Helper to preserve scroll position during updates
  const preserveScrollPosition = async (action: () => Promise<void>) => {
    const scrollY = window.scrollY;
    await action();
    // Use requestAnimationFrame to ensure DOM is updated before restoring scroll
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  };

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const unscheduledTasks = tasks.filter(t => t.task_type === 'unscheduled' || (!t.task_type && t.scheduled_hour === 0));
  const scheduledTasksAll = tasks.filter(t => t.task_type !== 'unscheduled' && (t.task_type || t.scheduled_hour > 0));
  const scheduledTasks = showMineOnly
    ? scheduledTasksAll.filter((t: any) => t.assigned_to_user_id === currentUserId)
    : scheduledTasksAll;
  const timeBlockTasks = scheduledTasks.filter(t => t.task_type === 'time_block');
  const hourlyTasks = scheduledTasks.filter(t => t.task_type !== 'time_block');

  const handleAddTask = async (taskData: CreateTaskRequest) => {
    await preserveScrollPosition(async () => {
      try {
        await apiService.createTask(taskData);
        await fetchTasks();
      } catch (error) {
        console.error('Failed to create task:', error);
        alert('Failed to create task');
      }
    });
  };

  const handleToggleComplete = async (task: Task) => {
    await preserveScrollPosition(async () => {
      try {
        const newPercentage = task.completion_percentage === 100 ? 0 : 100;
        await apiService.updateTaskCompletion(task.id, { completionPercentage: newPercentage });
        await fetchTasks();
      } catch (error) {
        console.error('Failed to update task:', error);
      }
    });
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Delete this task?')) return;
    await preserveScrollPosition(async () => {
      try {
        await apiService.deleteTask(taskId);
        await fetchTasks();
      } catch (error) {
        console.error('Failed to delete task:', error);
      }
    });
  };

  // Inline editing functions
  const startEditing = (task: Task) => {
    setEditingTaskId(task.id);
    setEditingTitle(task.title);
    setEditingNotes(task.description || '');
    setEditingDate((task.scheduled_date || '').split('T')[0]);
  };

  const cancelEditing = () => {
    setEditingTaskId(null);
    setEditingTitle('');
    setEditingNotes('');
    setEditingDate('');
  };

  const saveEditing = async (taskId: string) => {
    if (!isMountedRef.current) return; // Don't save if unmounting
    if (!editingTitle.trim()) {
      cancelEditing();
      return;
    }
    await preserveScrollPosition(async () => {
      try {
        if (!isMountedRef.current) return; // Double-check before API call
        await apiService.updateTask(taskId, {
          title: editingTitle.trim(),
          description: editingNotes.trim(),
          scheduledDate: editingDate || undefined
        });
        if (isMountedRef.current) {
          await fetchTasks();
          cancelEditing();
        }
      } catch (error) {
        console.error('Failed to update task:', error);
        if (isMountedRef.current) {
          alert('Failed to update task');
        }
      }
    });
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, taskId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEditing(taskId);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const renderInlineEditor = (task: Task, compact = false) => (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      data-testid={`inline-editor-${task.id}`}
      style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px', padding: compact ? '2px 0' : '4px 0' }}
    >
      <input
        type="text"
        value={editingTitle}
        onChange={(e) => setEditingTitle(e.target.value)}
        onKeyDown={(e) => handleEditKeyDown(e, task.id)}
        autoFocus
        placeholder="Task title"
        data-testid={`edit-task-input-${task.id}`}
        style={{ width: '100%', boxSizing: 'border-box', padding: compact ? '4px 8px' : '6px 10px', border: '2px solid var(--primary-color, #667eea)', borderRadius: '6px', fontSize: compact ? '0.78rem' : '0.9rem', fontWeight: 600, outline: 'none' }}
      />
      <textarea
        value={editingNotes}
        onChange={(e) => setEditingNotes(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') cancelEditing(); }}
        placeholder="Notes (optional)"
        rows={2}
        data-testid={`edit-task-notes-${task.id}`}
        style={{ width: '100%', boxSizing: 'border-box', padding: compact ? '4px 8px' : '6px 10px', border: '1px solid #c7d2fe', borderRadius: '6px', fontSize: compact ? '0.7rem' : '0.8rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit', color: '#374151' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: compact ? '0.68rem' : '0.75rem', color: '#6b7280', fontWeight: 600 }}>
          📅
          <input
            type="date"
            value={editingDate}
            onChange={(e) => setEditingDate(e.target.value)}
            onKeyDown={(e) => handleEditKeyDown(e, task.id)}
            data-testid={`edit-task-date-${task.id}`}
            style={{ padding: compact ? '2px 6px' : '4px 8px', border: '1px solid #c7d2fe', borderRadius: '6px', fontSize: compact ? '0.66rem' : '0.75rem', outline: 'none', color: '#374151' }}
          />
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          <button
            onClick={() => saveEditing(task.id)}
            data-testid={`save-task-edit-${task.id}`}
            style={{ padding: compact ? '3px 10px' : '5px 14px', backgroundColor: 'var(--primary-color, #667eea)', color: 'white', border: 'none', borderRadius: '6px', fontSize: compact ? '0.65rem' : '0.75rem', fontWeight: 700, cursor: 'pointer' }}
          >
            Save
          </button>
          <button
            onClick={cancelEditing}
            data-testid={`cancel-task-edit-${task.id}`}
            style={{ padding: compact ? '3px 10px' : '5px 14px', backgroundColor: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '6px', fontSize: compact ? '0.65rem' : '0.75rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  // Drag selection for creating bulk tasks
  const handleSlotMouseDown = (hour: number, half: 'first' | 'second') => {
    setDragSelection({ start: { hour, half }, end: { hour, half }, isSelecting: true });
  };

  const handleSlotMouseEnter = (hour: number, half: 'first' | 'second') => {
    if (dragSelection.isSelecting) {
      setDragSelection(prev => ({ ...prev, end: { hour, half } }));
    }
  };

  const handleSlotMouseUp = () => {
    if (dragSelection.isSelecting && dragSelection.start && dragSelection.end) {
      const startSlot = dragSelection.start;
      const endSlot = dragSelection.end;
      
      // Calculate actual start and end
      let startHour = Math.min(startSlot.hour, endSlot.hour);
      let startMinute = 0;
      let endHour = Math.max(startSlot.hour, endSlot.hour);
      let endMinute = 30;

      if (startSlot.hour === endSlot.hour) {
        startMinute = startSlot.half === 'first' ? 0 : 30;
        endMinute = endSlot.half === 'second' ? 60 : 30;
        if (startSlot.half === 'second' && endSlot.half === 'first') {
          startMinute = 0;
          endMinute = 60;
        }
      } else {
        startMinute = startSlot.hour < endSlot.hour 
          ? (startSlot.half === 'first' ? 0 : 30)
          : (endSlot.half === 'first' ? 0 : 30);
        endMinute = startSlot.hour > endSlot.hour
          ? (startSlot.half === 'second' ? 60 : 30)
          : (endSlot.half === 'second' ? 60 : 30);
      }

      if (endMinute === 60) {
        endHour += 1;
        endMinute = 0;
      }

      setPrefilledTime({
        start: { hour: startHour, minute: startMinute },
        end: { hour: endHour, minute: endMinute }
      });
      setShowAddModal(true);
    }
    setDragSelection({ start: null, end: null, isSelecting: false });
  };

  const isSlotSelected = (hour: number, half: 'first' | 'second') => {
    if (!dragSelection.start || !dragSelection.end) return false;
    
    const startIdx = dragSelection.start.hour * 2 + (dragSelection.start.half === 'second' ? 1 : 0);
    const endIdx = dragSelection.end.hour * 2 + (dragSelection.end.half === 'second' ? 1 : 0);
    const slotIdx = hour * 2 + (half === 'second' ? 1 : 0);
    
    return slotIdx >= Math.min(startIdx, endIdx) && slotIdx <= Math.max(startIdx, endIdx);
  };

  // Drag and drop for tasks
  const handleDragStart = (e: React.DragEvent, task: Task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetHour: number, targetHalf: 'first' | 'second') => {
    e.preventDefault();
    if (!draggedTask) return;

    try {
      const startMinute = targetHalf === 'first' ? 0 : 30;
      await apiService.updateTask(draggedTask.id, {
        taskType: 'hourly',
        scheduledHour: targetHour,
        startTime: `${targetHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}:00`,
        endTime: `${(targetHour + 1).toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}:00`
      });
      await fetchTasks();
    } catch (error) {
      console.error('Failed to schedule task:', error);
    }
    setDraggedTask(null);
  };

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours);
    const m = minutes;
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayHour}:${m} ${period}`;
  };

  const formatHour = (hour: number) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${period}`;
  };

  const getTimeBlockForHour = (hour: number) => {
    return timeBlockTasks.find(t => {
      const startHour = t.scheduled_hour || 0;
      let endHour = startHour + 1;
      if (t.end_time) {
        const parts = t.end_time.split(':');
        endHour = parseInt(parts[0]);
      }
      return hour >= startHour && hour < endHour;
    });
  };

  const getHourlyTasksForHour = (hour: number) => {
    return hourlyTasks.filter(t => t.scheduled_hour === hour);
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #e5e7eb', borderTop: '3px solid var(--primary-color, #667eea)', borderRadius: '50%', margin: '0 auto', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: `${rowWidth}px`, margin: '0 auto', padding: '20px', position: 'relative' }} data-testid="merged-planner">
      {/* Resize handle on the right */}
      <div
        onMouseDown={handleResizeStart}
        style={{
          position: 'fixed',
          right: `calc(50% - ${rowWidth / 2}px - 10px)`,
          top: '50%',
          transform: 'translateY(-50%)',
          width: '8px',
          height: '60px',
          backgroundColor: isResizing ? '#667eea' : '#d1d5db',
          borderRadius: '4px',
          cursor: 'ew-resize',
          transition: 'background-color 0.2s',
          zIndex: 100
        }}
        title="Drag to resize planner width"
      />
      
      {/* Header with Date Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={goToPreviousDay} style={{ padding: '10px 14px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem' }} data-testid="prev-day-btn">←</button>
          
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#1f2937' }}>
              {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h2>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              data-testid="date-picker"
              style={{ marginTop: '4px', padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem', color: '#6b7280' }}
            />
          </div>
          
          <button onClick={goToNextDay} style={{ padding: '10px 14px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem' }} data-testid="next-day-btn">→</button>
          
          {!isToday && (
            <button onClick={goToToday} style={{ padding: '8px 14px', backgroundColor: 'var(--primary-light, #f0f4ff)', color: 'var(--primary-color, #667eea)', border: '1px solid var(--primary-color, #667eea)', borderRadius: '8px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }} data-testid="today-btn">
              Today
            </button>
          )}
        </div>
        
        <button
          onClick={() => { setPrefilledTime({}); setShowAddModal(true); }}
          data-testid="add-task-btn"
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '12px 20px', backgroundColor: 'var(--primary-color, #667eea)',
            color: 'white', border: 'none', borderRadius: '10px',
            fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
          }}
        >
          + Add Task
        </button>
      </div>

      {/* Task Summary Stats */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '16px',
        flexWrap: 'wrap'
      }} data-testid="task-stats">
        <div style={{
          flex: 1,
          minWidth: '100px',
          padding: '12px 16px',
          backgroundColor: '#f0f4ff',
          borderRadius: '10px',
          textAlign: 'center',
          border: '1px solid #c7d2fe'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#667eea' }}>{tasks.length}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>Total Tasks</div>
        </div>
        <div style={{
          flex: 1,
          minWidth: '100px',
          padding: '12px 16px',
          backgroundColor: '#dcfce7',
          borderRadius: '10px',
          textAlign: 'center',
          border: '1px solid #86efac'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#22c55e' }}>
            {tasks.filter(t => t.completion_percentage === 100).length}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>Completed</div>
        </div>
        <div style={{
          flex: 1,
          minWidth: '100px',
          padding: '12px 16px',
          backgroundColor: '#fef3c7',
          borderRadius: '10px',
          textAlign: 'center',
          border: '1px solid #fcd34d'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f59e0b' }}>
            {tasks.filter(t => t.completion_percentage !== 100).length}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>Open</div>
        </div>
      </div>

      {/* Drag instruction banner */}
      <div style={{
        backgroundColor: '#fef3c7',
        border: '1px solid #f59e0b',
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '1.2rem' }}>💡</span>
        <span style={{ fontSize: '0.875rem', color: '#92400e' }}>
          <strong>Tips:</strong> Click &quot;Edit&quot; button on any task to modify it. Drag across time slots to create tasks, or drag from &quot;Today&apos;s Tasks&quot; to schedule them.
        </span>
      </div>

      {/* Today's Unscheduled Tasks */}
      <div style={{
        backgroundColor: 'var(--primary-light, #f0f4ff)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        border: '2px solid var(--primary-color, #667eea)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary-dark, #4c51bf)' }}>Today&apos;s Tasks</h3>
          <span style={{ fontSize: '0.875rem', color: 'var(--primary-color, #667eea)' }}>({unscheduledTasks.length})</span>
        </div>

        {unscheduledTasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--primary-dark, #4c51bf)' }}>
            <p style={{ margin: 0 }}>No unscheduled tasks. Add tasks without a specific time here!</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {unscheduledTasks.map(task => (
              <div
                key={task.id}
                draggable={editingTaskId !== task.id}
                onDragStart={(e) => editingTaskId !== task.id && handleDragStart(e, task)}
                data-testid={`unscheduled-task-${task.id}`}
                style={{
                  display: 'flex', alignItems: editingTaskId === task.id ? 'flex-start' : 'center', gap: '12px',
                  padding: '12px 16px',
                  backgroundColor: task.completion_percentage === 100 ? '#d1fae5' : (task.category_color ? `${task.category_color}15` : 'white'),
                  borderRadius: '8px', cursor: editingTaskId === task.id ? 'default' : 'grab',
                  border: task.category_color ? `2px solid ${task.category_color}` : '1px solid #e5e7eb',
                  transition: 'all 0.2s'
                }}
              >
                <input
                  type="checkbox"
                  checked={task.completion_percentage === 100}
                  onChange={() => handleToggleComplete(task)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                {editingTaskId === task.id ? (
                  renderInlineEditor(task)
                ) : (
                  <span 
                    onClick={(e) => {
                      // Single click - start editing after short delay to allow double-click detection
                      e.stopPropagation();
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      startEditing(task);
                    }}
                    style={{
                      flex: 1,
                      textDecoration: task.completion_percentage === 100 ? 'line-through' : 'none',
                      color: task.completion_percentage === 100 ? '#6b7280' : '#1f2937',
                      fontWeight: 500,
                      cursor: 'text',
                      padding: '4px 0'
                    }}
                    title="Double-click to edit"
                  >
                    {task.title}
                    {(task as any).push_count > 0 && (
                      <sup style={{ marginLeft: '4px', fontSize: '0.65rem', color: '#ef4444', fontWeight: 700, backgroundColor: '#fee2e2', padding: '1px 4px', borderRadius: '6px' }}>
                        {(task as any).push_count}x
                      </sup>
                    )}
                    {task.description && (
                      <span style={{ display: 'block', fontSize: '0.75rem', color: '#9ca3af', fontWeight: 400, marginTop: '2px' }}>
                        {task.description}
                      </span>
                    )}
                  </span>
                )}
                {editingTaskId !== task.id && (<>
                {getPriorityBadge(task.priority) && (
                  <span 
                    title={`${getPriorityBadge(task.priority)!.label} Priority`}
                    style={{ 
                      padding: '3px 8px', 
                      backgroundColor: getPriorityBadge(task.priority)!.bg, 
                      color: getPriorityBadge(task.priority)!.color, 
                      borderRadius: '10px', 
                      fontSize: '0.7rem', 
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px'
                    }}
                  >
                    {getPriorityBadge(task.priority)!.emoji}
                  </span>
                )}
                {task.category_name && task.category_color && (
                  <span style={{ padding: '4px 10px', backgroundColor: task.category_color, color: task.category_text_color || '#FFFFFF', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600 }}>
                    {task.category_name}
                  </span>
                )}
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); startEditing(task); }}
                  data-testid={`edit-task-${task.id}`}
                  title="Edit task"
                  style={{ background: '#f3f4f6', border: 'none', color: '#667eea', cursor: 'pointer', fontSize: '0.75rem', padding: '4px 10px', borderRadius: '6px', fontWeight: 600 }}
                >
                  Edit
                </button>
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); setRescheduleTask(task); }}
                  data-testid={`move-task-${task.id}`}
                  style={{ background: '#f3f4f6', border: 'none', color: '#667eea', cursor: 'pointer', fontSize: '0.75rem', padding: '4px 10px', borderRadius: '6px', fontWeight: 600 }}
                >
                  Move
                </button>
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                  style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '0.875rem', padding: '4px 8px' }}
                >
                  Delete
                </button>
                </>)}
              </div>
            ))}
          </div>
        )}
        
        <p style={{ margin: '12px 0 0 0', fontSize: '0.75rem', color: 'var(--primary-dark, #4c51bf)', textAlign: 'center' }}>
          Drag tasks to the schedule below to assign a time
        </p>
      </div>

      {/* Calendar-style schedule grid */}
      <div
        ref={gridRef}
        style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}
      >
        <div style={{ padding: '16px 20px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#374151' }}>Schedule</h3>
          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Drag empty space to create · drag a task to move · drag its bottom edge to resize</span>
          {(() => {
            const overlapCount = getOverlappingIds(scheduledTasks).size;
            return overlapCount > 0 ? (
              <span
                data-testid="planner-overlap-warning"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', fontWeight: 700, color: '#b91c1c', backgroundColor: '#fee2e2', border: '1px solid #fecaca', padding: '3px 8px', borderRadius: '10px' }}
              >
                ⚠️ {overlapCount} overlapping {overlapCount === 1 ? 'task' : 'tasks'}
              </span>
            ) : null;
          })()}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowMineOnly(!showMineOnly)}
              data-testid="my-tasks-toggle"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '8px',
                border: showMineOnly ? '1.5px solid #667eea' : '1px solid #e5e7eb',
                backgroundColor: showMineOnly ? '#eef2ff' : 'white',
                color: showMineOnly ? '#4338ca' : '#6b7280',
                fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer'
              }}
            >
              {showMineOnly ? '✅' : '👤'} My tasks
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 600 }}>Day</span>
              <select
                value={workStart}
                onChange={(e) => setWorkStart(Math.min(parseInt(e.target.value, 10), workEnd - 1))}
                data-testid="work-start-select"
                style={{ padding: '4px 6px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.7rem', color: '#374151' }}
              >
                {hours.map((h) => <option key={h} value={h}>{formatHour(h)}</option>)}
              </select>
              <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>–</span>
              <select
                value={workEnd}
                onChange={(e) => setWorkEnd(Math.max(parseInt(e.target.value, 10), workStart + 1))}
                data-testid="work-end-select"
                style={{ padding: '4px 6px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.7rem', color: '#374151' }}
              >
                {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => (
                  <option key={h} value={h}>{h === 24 ? '12 AM' : formatHour(h)}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 600 }}>Snap</span>
              <div style={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                {[5, 15, 30].map(val => (
                  <button
                    key={val}
                    onClick={() => setSnap(val)}
                    data-testid={`snap-option-${val}`}
                    style={{
                      padding: '5px 10px', border: 'none', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
                      backgroundColor: snap === val ? 'var(--primary-color, #667eea)' : 'white',
                      color: snap === val ? 'white' : '#6b7280',
                      borderLeft: val !== 5 ? '1px solid #e5e7eb' : 'none'
                    }}
                  >
                    {val}m
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', position: 'relative' }}>
          {/* Time labels */}
          <div style={{ width: '80px', flexShrink: 0, borderRight: '1px solid #f1f5f9' }}>
            {hours.map(hour => (
              <div key={hour} style={{ height: `${HOUR_HEIGHT}px`, padding: '4px 12px', color: '#6b7280', fontSize: '0.8rem', fontWeight: 500, boxSizing: 'border-box' }}>
                {formatHour(hour)}
              </div>
            ))}
          </div>

          {/* Event area */}
          <div ref={gridContentRef} style={{ flex: 1, position: 'relative', height: `${DAY_MINUTES * PX_PER_MIN}px` }}>
            {/* Gridlines */}
            {hours.map(hour => (
              <div key={hour} style={{ position: 'absolute', top: `${hour * HOUR_HEIGHT}px`, left: 0, right: 0, height: `${HOUR_HEIGHT}px`, borderBottom: '1px solid #f1f5f9', boxSizing: 'border-box' }}>
                <div style={{ position: 'absolute', top: `${HOUR_HEIGHT / 2}px`, left: 0, right: 0, borderBottom: '1px dashed #f1f5f9' }} />
              </div>
            ))}

            {/* Non-working hours dim */}
            {workStart > 0 && (
              <div data-testid="planner-dim-early" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: `${workStart * HOUR_HEIGHT}px`, backgroundColor: 'rgba(100,116,139,0.12)', zIndex: 6, pointerEvents: 'none' }} />
            )}
            {workEnd < 24 && (
              <div data-testid="planner-dim-late" style={{ position: 'absolute', top: `${workEnd * HOUR_HEIGHT}px`, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(100,116,139,0.12)', zIndex: 6, pointerEvents: 'none' }} />
            )}

            {/* Interaction layer: create-drag + drop target */}
            <div
              data-testid="planner-grid-surface"
              onMouseDown={bgMouseDown}
              onMouseMove={bgMouseMove}
              onMouseUp={bgMouseUp}
              onMouseLeave={() => createDragRef.current && bgMouseUp()}
              onDragOver={handleDragOver}
              onDrop={bgDrop}
              style={{ position: 'absolute', inset: 0, cursor: draggedTask ? 'copy' : 'crosshair' }}
            />

            {/* Create-drag preview */}
            {createDrag && (() => {
              const s = Math.min(createDrag.startMin, createDrag.curMin);
              const e = Math.max(createDrag.startMin, createDrag.curMin);
              return (
                <div style={{ position: 'absolute', top: `${s * PX_PER_MIN}px`, height: `${Math.max(snap, e - s) * PX_PER_MIN}px`, left: '4px', right: '4px', backgroundColor: '#dbeafe', border: '1px dashed #3b82f6', borderRadius: '6px', zIndex: 8, pointerEvents: 'none' }} />
              );
            })()}

            {/* Now line (only for today) */}
            {isToday && (
              <div
                ref={nowLineRef}
                data-testid="planner-now-line"
                style={{ position: 'absolute', top: `${nowMin * PX_PER_MIN}px`, left: 0, right: 0, height: '0', borderTop: '2px solid #ef4444', zIndex: 25, pointerEvents: 'none' }}
              >
                <div style={{ position: 'absolute', left: '-4px', top: '-5px', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ef4444' }} />
                <div style={{ position: 'absolute', left: '8px', top: '-9px', fontSize: '0.6rem', fontWeight: 700, color: '#ef4444', backgroundColor: 'white', padding: '0 3px', borderRadius: '3px' }}>
                  {minToTime(nowMin).slice(0, 5)}
                </div>
              </div>
            )}

            {/* Task blocks */}
            {(() => {
              const layout = computeLayout(scheduledTasks);
              const overlappingIds = getOverlappingIds(scheduledTasks);
              return scheduledTasks.map(task => {
                const preview = dragPreview && dragPreview.taskId === task.id ? dragPreview : null;
                const startMin = preview ? preview.startMin : getTaskStartMin(task);
                const endMin = preview ? preview.endMin : getTaskEndMin(task);
                const top = startMin * PX_PER_MIN;
                const isEditing = editingTaskId === task.id;
                const height = isEditing
                  ? Math.max(200, (endMin - startMin) * PX_PER_MIN)
                  : Math.max(snap * PX_PER_MIN, (endMin - startMin) * PX_PER_MIN);
                const pl = layout[task.id] || { col: 0, total: 1 };
                const widthPct = 100 / pl.total;
                const isDone = task.completion_percentage === 100;
                const isOverlap = overlappingIds.has(task.id);
                const accent = task.category_color || 'var(--primary-color, #667eea)';
                const isDragging = blockDrag && blockDrag.taskId === task.id;
                const short = !isEditing && height < 44;
                return (
                  <div
                    key={task.id}
                    data-testid={`planner-task-${task.id}`}
                    onMouseDown={(e) => startBlockMove(e, task)}
                    onDoubleClick={(e) => { e.stopPropagation(); startEditing(task); }}
                    style={{
                      position: 'absolute',
                      top: `${top}px`,
                      height: `${height}px`,
                      left: `calc(${pl.col * widthPct}% + 4px)`,
                      width: `calc(${widthPct}% - 10px)`,
                      backgroundColor: isDone ? '#f0fdf4' : (isOverlap ? '#fef2f2' : (task.category_color ? `${task.category_color}1f` : '#eef2ff')),
                      borderLeft: `4px solid ${isOverlap ? '#ef4444' : accent}`,
                      outline: isOverlap ? '1.5px dashed #ef4444' : 'none',
                      outlineOffset: '-1.5px',
                      borderRadius: '6px',
                      padding: short ? '2px 6px' : '6px 8px',
                      boxSizing: 'border-box',
                      overflow: 'hidden',
                      zIndex: isDragging ? 30 : (isEditing ? 40 : 10),
                      boxShadow: isDragging ? '0 8px 20px rgba(0,0,0,0.18)' : (isEditing ? '0 6px 18px rgba(0,0,0,0.15)' : '0 1px 2px rgba(0,0,0,0.06)'),
                      cursor: isEditing ? 'default' : 'grab',
                      userSelect: 'none',
                      opacity: isDragging ? 0.9 : 1,
                      transition: isDragging ? 'none' : 'box-shadow 0.15s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', height: '100%' }}>
                      <input
                        type="checkbox"
                        checked={isDone}
                        onChange={() => handleToggleComplete(task)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '14px', height: '14px', flexShrink: 0, marginTop: '2px' }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {isEditing ? (
                          renderInlineEditor(task, true)
                        ) : (
                          <div style={{ fontSize: '0.8rem', fontWeight: 600, textDecoration: isDone ? 'line-through' : 'none', color: isDone ? '#6b7280' : '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={isOverlap ? `${task.title} (overlaps another task)` : task.title}>
                            {isOverlap && (
                              <span style={{ marginRight: '4px', fontSize: '0.7rem' }} title="This task overlaps another">⚠️</span>
                            )}
                            {getPriorityBadge(task.priority) && (
                              <span style={{ marginRight: '4px', fontSize: '0.7rem' }}>{getPriorityBadge(task.priority)!.emoji}</span>
                            )}
                            {task.title}
                            {(task as any).push_count > 0 && (
                              <sup style={{ marginLeft: '3px', fontSize: '0.5rem', color: '#ef4444', fontWeight: 700, backgroundColor: '#fee2e2', padding: '1px 2px', borderRadius: '3px' }}>{(task as any).push_count}x</sup>
                            )}
                          </div>
                        )}
                        {!short && !isEditing && (
                          <div style={{ fontSize: '0.65rem', color: '#6b7280', marginTop: '2px' }}>
                            {minToTime(startMin).slice(0, 5)} - {minToTime(endMin).slice(0, 5)}
                          </div>
                        )}
                        {!short && !isEditing && (task as any).assigned_to_user_id === currentUserId && (task as any).user_id && (task as any).user_id !== currentUserId && (
                          <div
                            data-testid={`assigned-by-note-${task.id}`}
                            style={{ display: 'inline-block', marginTop: '3px', fontSize: '0.6rem', fontWeight: 600, color: '#2563eb', backgroundColor: '#dbeafe', padding: '1px 6px', borderRadius: '8px' }}
                          >
                            👤 assigned by {(task as any).assigned_by_first_name || (task as any).assigned_by_username || 'a teammate'}
                          </div>
                        )}
                      </div>
                      {!short && editingTaskId !== task.id && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
                          <button onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); startEditing(task); }} style={{ background: '#ffffffcc', border: 'none', color: '#667eea', cursor: 'pointer', padding: '2px 4px', borderRadius: '3px', fontSize: '0.55rem', fontWeight: 600 }}>Edit</button>
                          <button onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); setRescheduleTask(task); }} style={{ background: '#ffffffcc', border: 'none', color: '#667eea', cursor: 'pointer', padding: '2px 4px', borderRadius: '3px', fontSize: '0.55rem', fontWeight: 600 }}>Move</button>
                          <button onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '1px', fontSize: '0.75rem' }}>×</button>
                        </div>
                      )}
                    </div>

                    {/* Resize handle */}
                    {!isEditing && (
                    <div
                      data-testid={`planner-task-resize-${task.id}`}
                      onMouseDown={(e) => startBlockResize(e, task)}
                      style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '9px', cursor: 'ns-resize', display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}
                    >
                      <div style={{ width: '30px', height: '3px', borderRadius: '2px', backgroundColor: accent, opacity: 0.5, marginBottom: '1px' }} />
                    </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>


      {/* Empty State */}
      {tasks.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', backgroundColor: '#f9fafb', borderRadius: '12px', marginTop: '24px' }}>
          <h3 style={{ margin: '0 0 12px 0', color: '#374151' }}>Start your day!</h3>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>Add your first task to begin planning</p>
          <button
            onClick={() => { setPrefilledTime({}); setShowAddModal(true); }}
            style={{ padding: '14px 28px', backgroundColor: 'var(--primary-color, #667eea)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer' }}
          >
            + Add Your First Task
          </button>
        </div>
      )}

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setPrefilledTime({}); }}
        onSubmit={handleAddTask}
        selectedDate={selectedDate}
        categories={categories}
        prefilledStart={prefilledTime.start}
        prefilledEnd={prefilledTime.end}
      />

      {/* Reschedule Modal */}
      <RescheduleModal
        isOpen={!!rescheduleTask}
        onClose={() => setRescheduleTask(null)}
        onReschedule={handleReschedule}
        taskTitle={rescheduleTask?.title || ''}
        currentDate={selectedDate}
        currentHour={rescheduleTask?.scheduled_hour}
        currentStartTime={rescheduleTask?.start_time}
        currentEndTime={rescheduleTask?.end_time}
      />
    </div>
  );
};

export default MergedPlanner;
